// MultiChat — the unified chat surface for HomeAI.
//
// Single-model flow: each prompt streams a reply from one selected
// model. Users can switch the model from the composer dropdown.
//
// All models route through the Lovable AI Gateway via `ai-route`.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { ArrowUp, Square, MessageSquare, X, Check, Sparkles, Zap, Gem, ChevronDown, RotateCw, Brain, Bug, Image as ImageIcon, Globe, BarChart3, Code2, Bot, Paperclip, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  MODELS_BY_CAPABILITY,
  findModel,
  type ModelOption,
} from "@/lib/aiModels";
import ImageResult, { type ImageResultState } from "./results/ImageResult";
import AgentResult, { type AgentResultState } from "./results/AgentResult";
import ResultActions from "./results/ResultActions";
import { promptToFilename, saveText } from "@/lib/downloads";
import { useNavigate } from "react-router-dom";

type Role = "user" | "assistant";

/** Operating "kind" of the next assistant turn. Chat-style kinds reuse
 *  the streaming text path with different model defaults; image/agent
 *  swap in their own dedicated renderer component. */
export type ChatKind = "chat" | "research" | "analyze" | "code" | "image" | "agent";

const TEXT_KINDS: ChatKind[] = ["chat", "research", "analyze", "code"];

const KIND_META: Record<ChatKind, { label: string; Icon: any; placeholder: string }> = {
  chat:     { label: "Chat",     Icon: MessageSquare, placeholder: "Ask anything…" },
  research: { label: "Research", Icon: Globe,         placeholder: "What do you want to research?" },
  analyze:  { label: "Analyze",  Icon: BarChart3,     placeholder: "Paste data, logs, or a doc to analyze…" },
  code:     { label: "Code",     Icon: Code2,         placeholder: "What do you want to code?" },
  image:    { label: "Image",    Icon: ImageIcon,     placeholder: "Describe an image to generate…" },
  agent:    { label: "Agent",    Icon: Bot,           placeholder: "What should your agent do?" },
};

/** Cheap keyword router used when the user hasn't picked a mode.
 *  Order matters: image and agent win over the text kinds. */
function detectKind(text: string): ChatKind {
  const t = text.toLowerCase();
  if (/\b(image|picture|photo|draw|render|illustration|logo|poster|wallpaper|generate an? image)\b/.test(t)) return "image";
  if (/\b(agent|automation|bot that|monitor|watch (for|hn|reddit|twitter)|every (day|hour|morning)|alert me|notify me)\b/.test(t)) return "agent";
  if (/\b(research|cite|citations?|sources?|latest|news|today'?s|this week|recent)\b/.test(t)) return "research";
  if (/\b(analyze|analyse|csv|spreadsheet|chart|graph|statistics|dataset|trend|summari[sz]e (this|the))\b/.test(t)) return "analyze";
  if (/\b(code|function|class|bug|debug|stack trace|typescript|javascript|python|rust|go(lang)?|sql|regex|api|endpoint|refactor)\b/.test(t)) return "code";
  return "chat";
}

/** Meta-question detector: catches "what can I do here", "how does this work",
 *  "show me the roadmap", etc. Returning true short-circuits the LLM and
 *  drops a curated, instant walkthrough into the transcript. */
function detectMeta(text: string): boolean {
  const t = text.toLowerCase().trim();
  if (!t) return false;
  return (
    /^\/?(help|tour|onboarding|roadmap|whats?\s*new)\b/.test(t) ||
    /\bwhat\s+(can|do)\s+i\s+do\s+here\b/.test(t) ||
    /\bwhat\s+is\s+this(\s+(site|app|place))?\b/.test(t) ||
    /\bhow\s+(does|do)\s+(this|it|the\s+site)\s+work\b/.test(t) ||
    /\bhow\s+do\s+i\s+(use|start)\b/.test(t) ||
    /\b(what'?s|whats)\s+here\b/.test(t) ||
    /\bshow\s+me\s+(around|the\s+roadmap)\b/.test(t) ||
    /\bget(ting)?\s+started\b/.test(t)
  );
}

/** Canned walkthrough rendered as an assistant markdown message.
 *  Instant, free, always accurate — no model call. */
const WALKTHROUGH_MD = `**Welcome to hackerlabs.ai — one bar, every AI.**

Six modes below the bar. Pick one, or just type and we'll route it.

- **Chat** — explain anything, like a smart pair.
- **Research** — Perplexity Sonar with live citations.
- **Analyze** — paste logs, CSVs, or scan output for triage.
- **Code** — full programs, refactors, debug help.
- **Image** — generate art, diagrams, mockups.
- **Agent** — design an automation that runs on a schedule.

**Try one now**
1. \`Top 3 critical OpenSSL CVEs in 2024\` → Research
2. \`Cyberpunk hacker workspace, neon green\` → Image
3. \`Build an agent that watches HN for AI-safety stories\` → Agent

**Your roadmap**
- Earn points by running prompts and shipping work
- Unlock tiers and certifications you can post on LinkedIn
- Join squads and climb the weekly leaderboard
- Fork community playbooks from the Master Library

Open **Your journey** in the top-right menu to see Mastery, Squads, and the Library anytime. Ask "what can I do here?" in the bar whenever you need a refresher.`;

type Message = {
  id: string;
  role: Role;
  text: string;
  model?: string | null;       // for assistant: which model produced it
  status?: "streaming" | "done" | "error";
  error?: string | null;
  /** Which renderer owns this turn. Defaults to "chat". */
  kind?: ChatKind;
  /** Inline state for kind="image". */
  image?: ImageResultState;
  /** Inline state for kind="agent". */
  agent?: AgentResultState;
};

const TIER_ICON = { cheapest: Zap, balanced: Sparkles, premium: Gem } as const;

let mid = 0;
const nextId = () => `m_${Date.now()}_${++mid}`;

/** Build the available model list for the picker. */
function useAvailableModels() {
  return useMemo(() => {
    const lovable = MODELS_BY_CAPABILITY.chat;
    return { lovable, all: lovable };
  }, []);
}

/** Stream a single chat response via the `ai-route` edge function. */
async function streamOne(opts: {
  modelId: string;
  messages: { role: Role; text: string }[];
  signal: AbortSignal;
  onDelta: (chunk: string) => void;
}): Promise<void> {
  const { modelId, messages, signal, onDelta } = opts;
  const { data: { session } } = await supabase.auth.getSession();
  const authHeader = session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {};
  const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  // ai-route takes a single prompt today, so collapse the convo
  // by passing the latest user turn (it doesn't keep a thread yet).
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-route`;
  const lastUser = [...messages].reverse().find((m) => m.role === "user");
  const body = { prompt: lastUser?.text ?? "", model: modelId };

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey, ...authHeader },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const t = await res.text().catch(() => "");
    throw new Error(t || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      try {
        const evt = JSON.parse(payload);
        const delta = evt.choices?.[0]?.delta?.content;
        if (typeof delta === "string" && delta.length) onDelta(delta);
      } catch { /* ignore parse errors on intermediate events */ }
    }
  }
}

export default function MultiChat() {
  const navigate = useNavigate();
  /** Which "kind" the composer will produce next. */
  const [kind, setKind] = useState<ChatKind>("chat");
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [model, setModel] = useState<string>("google/gemini-3-flash-preview");
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  /** False until the user explicitly picks a mode (via FunctionStrip).
   *  While false, placeholder stays generic and we auto-route on send. */
  const [kindTouched, setKindTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState<{ id: string; name: string; size: number; type: string; text?: string; dataUrl?: string }[]>([]);
  const abortRef = useRef<AbortController | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const { all, lovable } = useAvailableModels();

  // Autosize textarea + autofocus
  useEffect(() => {
    const el = taRef.current; if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [input]);
  useEffect(() => { taRef.current?.focus(); }, []);
  // Allow other surfaces (suggestion chips, library) to seed the composer.
  useEffect(() => {
    const onSeed = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === "string") {
        setInput(detail);
        setTimeout(() => taRef.current?.focus(), 0);
      }
    };
    window.addEventListener("multichat-seed", onSeed as EventListener);
    const onSetMode = (e: Event) => {
      const detail = (e as CustomEvent<{ kind?: ChatKind; modelId?: string }>).detail || {};
      if (detail.kind) { setKind(detail.kind); setKindTouched(true); }
      if (detail.modelId) setModel(detail.modelId);
      setTimeout(() => taRef.current?.focus(), 0);
    };
    window.addEventListener("multichat-set-mode", onSetMode as EventListener);
    return () => {
      window.removeEventListener("multichat-seed", onSeed as EventListener);
      window.removeEventListener("multichat-set-mode", onSetMode as EventListener);
    };
  }, []);
  useEffect(() => {
    const el = scrollerRef.current; if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Tell the page when the chat is "active" (user has typed, sent, or
  // is streaming a response) so chrome like the footer can slide out
  // of the way. Idle = empty input + no messages + not busy.
  useEffect(() => {
    const active = busy || messages.length > 0 || input.trim().length > 0;
    window.dispatchEvent(
      new CustomEvent("multichat-activity", { detail: { active } }),
    );
  }, [busy, messages.length, input]);

  const updateMsg = useCallback((id: string, patch: (m: Message) => Message) => {
    setMessages((arr) => arr.map((m) => m.id === id ? patch(m) : m));
  }, []);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setBusy(false);
  }, []);

  const send = useCallback(async () => {
    const text = input.trim();
    if ((!text && attachments.length === 0) || busy) return;
    // Slash-command-style shortcut: typing "roadmap" jumps the user
    // straight into the points/badges/leaderboard journey.
    if (/^\/?roadmap\b/i.test(text) && attachments.length === 0) {
      setInput("");
      toast.success("Opening your roadmap — points, badges & leaderboard");
      navigate("/roadmap");
      return;
    }
    // Meta-question intercept: "what can I do here", "how does this work",
    // "show me the roadmap", etc. Drop a canned walkthrough into the
    // transcript instead of paying a model to hallucinate about us.
    if (attachments.length === 0 && detectMeta(text)) {
      const userMsg: Message = { id: nextId(), role: "user", text };
      const aMsg: Message = {
        id: nextId(),
        role: "assistant",
        text: WALKTHROUGH_MD,
        status: "done",
        kind: "chat",
      };
      setMessages((arr) => [...arr, userMsg, aMsg]);
      setInput("");
      return;
    }
    // Auto-route: if the user never picked a mode, guess from the text
    // so they end up on the right surface (image / agent / research / …).
    const effectiveKind: ChatKind = kindTouched ? kind : detectKind(text);
    if (!kindTouched && effectiveKind !== kind) setKind(effectiveKind);
    const attachmentBlock = attachments.length
      ? "\n\n" + attachments.map((a) => {
          if (a.text) return `--- attached: ${a.name} ---\n${a.text}`;
          if (a.dataUrl) return `--- attached image: ${a.name} ---`;
          return `--- attached file: ${a.name} (${Math.round(a.size / 1024)} KB) ---`;
        }).join("\n\n")
      : "";
    const composed = text + attachmentBlock;
    const userMsg: Message = { id: nextId(), role: "user", text: composed || "(attachments only)" };
    const history = [...messages, userMsg];
    setMessages(history);
    setInput("");
    setAttachments([]);
    setBusy(true);
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    // Image and Agent kinds use dedicated renderers that own their
    // own async work. The composer just plants the assistant message
    // with seed state; the renderer fires the actual call on mount.
    if (effectiveKind === "image") {
      // If the user attached an image, use it as the source for
      // image-to-image editing/remixing via the Nano Banana models.
      const imgAttachment = attachments.find((a) => a.dataUrl);
      setMessages((arr) => [
        ...arr,
        {
          id: nextId(),
          role: "assistant",
          text: "",
          kind: "image",
          status: "done",
          image: {
            prompt: text,
            imageUrl: null,
            status: "loading",
            sourceImageUrl: imgAttachment?.dataUrl ?? null,
          },
        },
      ]);
      setBusy(false);
      return;
    }
    if (effectiveKind === "agent") {
      setMessages((arr) => [
        ...arr,
        {
          id: nextId(),
          role: "assistant",
          text: "",
          kind: "agent",
          status: "done",
          agent: { prompt: text, status: "loading", draft: null, savedAgentId: null },
        },
      ]);
      setBusy(false);
      return;
    }

    // Single-model: stream one reply from the selected model.
    const aId = nextId();
    setMessages((arr) => [...arr, { id: aId, role: "assistant", text: "", status: "streaming", model, kind: effectiveKind }]);
    const startedAt = performance.now();
    try {
      await streamOne({
        modelId: model,
        messages: history.map(({ role, text }) => ({ role, text })),
        signal,
        onDelta: (chunk) => updateMsg(aId, (m) => ({ ...m, text: m.text + chunk })),
      });
      updateMsg(aId, (m) => ({ ...m, status: "done" }));
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        updateMsg(aId, (m) => ({ ...m, status: "error", error: e?.message ?? "Failed" }));
      }
    }
    void startedAt;
    setBusy(false);
  }, [input, attachments, busy, messages, kind, kindTouched, model, updateMsg, navigate]);

  const reset = () => { stop(); setMessages([]); };

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !busy) send();
    }
  };

  return (
    <div className="w-full">
      {/* Kind chip + compare toggle. The kind tells the next turn what
          surface to render (chat / research / image / agent / …); the
          compare toggle only applies to text kinds. */}
      <div className="flex items-center justify-end mb-3 px-1">
        {messages.length > 0 && (
          <button
            type="button"
            onClick={reset}
            className="h-7 px-2 inline-flex items-center gap-1 rounded-md text-[10px] font-mono text-muted-foreground hover:text-foreground hover:bg-secondary/60"
          >
            <RotateCw className="w-3 h-3" /> New chat
          </button>
        )}
      </div>

      {/* Transcript */}
      {messages.length > 0 && (
        <div ref={scrollerRef} className="mb-3 max-h-[55vh] overflow-y-auto rounded-2xl border border-border bg-card/40 p-3 space-y-4">
          {messages.map((m) => (
            <MessageRow key={m.id} message={m} onPatch={updateMsg} />
          ))}
        </div>
      )}


      {/* Composer */}
      <div className={cn(
        "relative w-full rounded-3xl border bg-card/60 backdrop-blur transition-all",
        busy ? "border-primary/40" : "border-border focus-within:border-primary/60 focus-within:shadow-[0_0_40px_hsl(var(--primary)/0.25)]",
      )}>
        <textarea
          ref={taRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          placeholder={kindTouched ? KIND_META[kind].placeholder : "What do you want to do?"}
          className="w-full resize-none bg-transparent px-5 pt-4 pb-14 text-base text-foreground placeholder:text-muted-foreground/70 outline-none"
        />
        {attachments.length > 0 && (
          <div className="px-4 pb-2 flex flex-wrap gap-1.5">
            {attachments.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1.5 h-7 pl-2 pr-1 rounded-full border border-border bg-secondary/60 text-[11px] font-mono max-w-[220px]">
                <FileText className="w-3 h-3 text-primary shrink-0" />
                <span className="truncate">{a.name}</span>
                <button
                  type="button"
                  onClick={() => setAttachments((arr) => arr.filter((x) => x.id !== a.id))}
                  className="h-5 w-5 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary"
                  aria-label={`Remove ${a.name}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={async (e) => {
            const files = Array.from(e.target.files ?? []);
            for (const f of files) {
              const id = `a_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
              const isImage = f.type.startsWith("image/");
              const isText = f.type.startsWith("text/") || /\.(md|txt|json|csv|tsv|ya?ml|toml|js|ts|tsx|jsx|py|rb|go|rs|java|c|cpp|html|css)$/i.test(f.name);
              try {
                if (isText && f.size < 1_000_000) {
                  const text = await f.text();
                  setAttachments((arr) => [...arr, { id, name: f.name, size: f.size, type: f.type, text }]);
                } else if (isImage && f.size < 5_000_000) {
                  const dataUrl = await new Promise<string>((res, rej) => {
                    const r = new FileReader();
                    r.onload = () => res(r.result as string);
                    r.onerror = () => rej(r.error);
                    r.readAsDataURL(f);
                  });
                  setAttachments((arr) => [...arr, { id, name: f.name, size: f.size, type: f.type, dataUrl }]);
                } else {
                  setAttachments((arr) => [...arr, { id, name: f.name, size: f.size, type: f.type }]);
                  toast.message(`Attached ${f.name} (content not inlined)`);
                }
              } catch (err: any) {
                toast.error(`Couldn't read ${f.name}`);
              }
            }
            if (fileRef.current) fileRef.current.value = "";
          }}
        />
        <div className="absolute left-3 bottom-3 right-3 flex items-center justify-between gap-2">
          {/* Model controls — image/agent run on a fixed surface, so no picker. */}
          {!TEXT_KINDS.includes(kind) ? (
            <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-border/70 bg-card/60 text-[11px] font-mono text-muted-foreground">
              {(() => { const I = KIND_META[kind].Icon; return <I className="w-3 h-3 text-primary" />; })()}
              {KIND_META[kind].label} mode
            </span>
          ) : (
            <SingleModelPicker
              selected={model}
              onPick={setModel}
              open={modelPickerOpen}
              setOpen={setModelPickerOpen}
              lovable={lovable}
            />
          )}
          <div className="flex items-center gap-2">
            {/* Quick access — Brain (your memory) and Report a bug.
                Kept inside the prompt so users never leave home. Bug
                reports route into the feedback queue admins see. */}
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label="Attach files"
              title="Attach files"
              className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            >
              <Paperclip className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("open-memory"))}
              aria-label="Brain — your memory"
              title="Brain — your memory"
              className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            >
              <Brain className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("open-bug"))}
              aria-label="Report a bug"
              title="Report a bug — goes straight to admin"
              className="h-8 w-8 grid place-items-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors"
            >
              <Bug className="w-4 h-4" />
            </button>
            {busy ? (
              <button
                type="button"
                onClick={stop}
                aria-label="Stop"
                className="h-9 w-9 grid place-items-center rounded-full bg-secondary text-foreground hover:bg-secondary/80"
              >
                <Square className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() && attachments.length === 0}
                aria-label="Send"
                className={cn(
                  "h-9 w-9 grid place-items-center rounded-full transition-all",
                  input.trim() || attachments.length > 0
                    ? "bg-primary text-primary-foreground hover:scale-105 shadow-[0_0_20px_hsl(var(--primary)/0.4)]"
                    : "bg-secondary text-muted-foreground cursor-not-allowed",
                )}
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

// --- Subcomponents -----------------------------------------------------------

function ModelTierIcon({ model }: { model: ModelOption | null }) {
  if (!model) return null;
  const I = TIER_ICON[model.tier];
  return <I className="w-3 h-3 text-primary" />;
}

function SingleModelPicker(props: {
  selected: string;
  onPick: (id: string) => void;
  open: boolean;
  setOpen: (v: boolean) => void;
  lovable: ModelOption[];
}) {
  const { selected, onPick, open, setOpen, lovable } = props;
  const m = findModel(selected);
  return (
    <div className="relative flex items-center min-w-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border border-border bg-card/60 hover:border-primary/50 text-[11px] font-mono"
      >
        <ModelTierIcon model={m} />
        <span className="truncate max-w-[160px]">{m?.label ?? selected}</span>
        <ChevronDown className="w-3 h-3 text-muted-foreground" />
      </button>
      {open && (
        <ModelMenu
          lovable={lovable}
          onPick={(id) => { onPick(id); setOpen(false); }}
          isSelected={(id) => id === selected}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function ModelMenu(props: {
  lovable: ModelOption[];
  onPick: (id: string) => void;
  isSelected: (id: string) => boolean;
  onClose: () => void;
  multi?: boolean;
}) {
  const { lovable, onPick, isSelected, onClose, multi } = props;
  return (
    <>
      <button className="fixed inset-0 z-30" aria-hidden onClick={onClose} />
      <div className="absolute z-40 mt-2 bottom-full mb-2 left-0 w-80 max-h-[60vh] overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-xl p-1.5">
        <Section title="Lovable AI · included">
          {lovable.map((m) => (
            <ModelRow key={m.id} m={m} selected={isSelected(m.id)} onClick={() => onPick(m.id)} multi={multi} />
          ))}
        </Section>
      </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <div className="px-2 py-1 text-[9px] uppercase tracking-wider font-mono text-muted-foreground/70">{title}</div>
      {children}
    </div>
  );
}

function ModelRow({ m, selected, onClick, multi }: { m: ModelOption; selected: boolean; onClick: () => void; multi?: boolean }) {
  const I = TIER_ICON[m.tier];
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-start gap-2 px-2 py-2 rounded-lg text-left hover:bg-secondary/60 transition-colors",
        selected && "bg-secondary/70",
      )}
    >
      <I className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-mono truncate">{m.label}</span>
          <span className="text-[10px] font-mono text-muted-foreground shrink-0">
            {m.pricePer1M > 0 ? `$${m.pricePer1M.toFixed(2)}/1M` : "—"}
          </span>
        </div>
        <div className="text-[10px] text-muted-foreground truncate">{m.blurb}</div>
      </div>
      {multi && selected && <Check className="w-3.5 h-3.5 text-primary shrink-0" />}
    </button>
  );
}

function MessageRow({ message, onPatch }: {
  message: Message;
  onPatch: (id: string, patch: (m: Message) => Message) => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl bg-primary text-primary-foreground px-4 py-2 text-sm whitespace-pre-wrap">
          {message.text}
        </div>
      </div>
    );
  }
  // Image and Agent kinds delegate to dedicated renderers that own
  // their own async work and download buttons.
  if (message.kind === "image" && message.image) {
    return (
      <ImageResult
        state={message.image}
        onPatch={(patch) => onPatch(message.id, (m) => ({ ...m, image: { ...(m.image ?? message.image!), ...patch } }))}
        onRegenerate={() => onPatch(message.id, (m) => ({
          ...m,
          image: {
            prompt: m.image?.prompt ?? "",
            imageUrl: null,
            status: "loading",
            sourceImageUrl: m.image?.sourceImageUrl ?? null,
          },
        }))}
      />
    );
  }
  if (message.kind === "agent" && message.agent) {
    return (
      <AgentResult
        state={message.agent}
        onPatch={(patch) => onPatch(message.id, (m) => ({ ...m, agent: { ...(m.agent ?? message.agent!), ...patch } }))}
        onRegenerate={() => onPatch(message.id, (m) => ({ ...m, agent: { prompt: m.agent?.prompt ?? "", status: "loading", draft: null, savedAgentId: null } }))}
      />
    );
  }
  // Assistant — single answer
  const m = message.model ? findModel(message.model) : null;
  const ext = "md";
  const fname = `${promptToFilename(message.text.slice(0, 40) || (message.kind ?? "chat"))}.${ext}`;
  return (
    <div className="space-y-1">
      <div className="prose prose-sm dark:prose-invert max-w-none">
        {message.text ? <ReactMarkdown>{message.text}</ReactMarkdown> : <span className="text-muted-foreground text-xs">…</span>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {m && (
          <span className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground">
            <ModelTierIcon model={m} /> {m.label}
          </span>
        )}
        {message.status === "error" && (
          <span className="text-[10px] text-destructive font-mono">{message.error}</span>
        )}
        {message.text && message.status !== "streaming" && (
          <ResultActions
            copyText={message.text}
            onDownload={() => saveText(message.text, fname, "text/markdown")}
            downloadLabel="Download .md"
          />
        )}
      </div>
    </div>
  );
}
